<script>
import Swal from "sweetalert2";
import { cloneDeep, groupBy } from "lodash";

export default {
    name: "ChangeExpenditureModal",
    props: {
        budgets: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            showModal: false,
            selectedResource: null,
            filterBudget: "",
            availableAccounts: [],
            budgetBusy: false,
        };
    },
    computed: {
        filteredBudget() {
            let data = this.availableAccounts;
            if (this.filterBudget && this.filterBudget.trim() !== "") {
                data = data.filter((item) => {
                    return item.filterKey
                        .toUpperCase()
                        .includes(this.filterBudget.toUpperCase());
                });
            }
            return data;
        },
    },
    watch: {
        budgets: {
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.formatBudgets(newVal);
                }
            },
        },
    },
    methods: {
        open(resource) {
            this.selectedResource = resource;
            this.filterBudget = "";
            this.showModal = true;
            this.$bvModal.show("change-expenditure-modal");
            if (this.availableAccounts.length === 0) {
                if (this.budgets && this.budgets.length > 0) {
                    this.formatBudgets(this.budgets);
                } else {
                    this.loadAvailableAccounts();
                }
            }
        },
        closeModal() {
            this.showModal = false;
            this.$bvModal.hide("change-expenditure-modal");
        },
        formatBudgets(budgets) {
            if (!budgets || !Array.isArray(budgets)) return;
            let expenses = budgets.filter((x) =>
                !x.accountType || x.accountType.toLowerCase().includes("expense")
            );
            let grp = groupBy(expenses, "fundClassificationId");
            let data = [];
            for (const [key, value] of Object.entries(grp)) {
                if (!value || value.length === 0) continue;
                const fundClass = value[0].fundClassification || { classification: "Fund " + key };
                let filterKey =
                    (fundClass.classification || "") +
                    " " +
                    value.map((x) => x.groupName || "").join(" ");
                data.push({
                    fundClassificationId: key,
                    fundClassification: fundClass,
                    accounts: value,
                    showData: false,
                    filterKey: filterKey,
                });
            }
            this.availableAccounts = data;
        },
        async loadAvailableAccounts() {
            this.budgetBusy = true;
            try {
                const response = await this.$store.dispatch(
                    "accountgroups/getAllAccountGroup"
                );
                if (response && response.error) {
                    this.budgetBusy = false;
                    return this.showToast(response.errorMessage, "error");
                }
                const items = Array.isArray(response?.data)
                    ? response.data
                    : response?.data?.data || [];
                this.formatBudgets(items);
            } catch (err) {
                this.showToast("Cannot fetch budget accounts", "error");
            } finally {
                this.budgetBusy = false;
            }
        },
        filteredBudgetChild(info) {
            let data = cloneDeep(info);
            if (this.filterBudget && this.filterBudget.trim() !== "") {
                data = data.filter((item) => {
                    return item.groupName
                        .toUpperCase()
                        .includes(this.filterBudget.toUpperCase());
                });
            }
            return data;
        },
        transferIfs(row) {
            if (!this.selectedResource) return false;
            const currentAccountId =
                this.selectedResource.accountGroupId ||
                this.selectedResource.financeTemplateBudgetItem?.accountId ||
                this.selectedResource.financeTemplateBudgetItem?.id;
            return currentAccountId != row.id;
        },
        async transferHere(z) {
            if (!this.transferIfs(z)) return;
            Swal.fire({
                title: "Are you sure?",
                text: `You want to transfer "${this.selectedResource.itemName}" to ${z.groupName}?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#34c38f",
                cancelButtonColor: "#f46a6a",
                confirmButtonText: "Yes, transfer it!",
            }).then(async (result) => {
                if (result.value || result.isConfirmed) {
                    this.budgetBusy = true;
                    try {
                        let data = cloneDeep(this.selectedResource);
                        data.accountGroupId = z.id;
                        const res = await this.$store.dispatch(
                            "planningapiresource/UpdatePlanningApiResource",
                            data
                        );
                        if (res?.data?.error || res?.error) {
                            this.budgetBusy = false;
                            return this.showToast(res?.data?.errorMessage || res?.errorMessage || "Failed to transfer item", "error");
                        }

                        // Update local object reactively
                        this.$set(this.selectedResource, "accountGroupId", z.id);
                        const updatedFinanceItem = {
                            fundSource: z.fundSource,
                            fundClass: z.fundClassification
                                ? z.fundClassification.classification
                                : "",
                            fundClassificationId: z.fundClassificationId,
                            fundClassification: z.fundClassification,
                            groupName: z.groupName,
                            accountGroup: z.groupName,
                            accountId: z.id,
                            id: z.id,
                        };
                        this.$set(
                            this.selectedResource,
                            "financeTemplateBudgetItem",
                            updatedFinanceItem
                        );

                        this.showToast("Expenditure transferred successfully!", "success");
                        this.$emit("updated", {
                            resource: this.selectedResource,
                            account: z,
                        });
                        this.closeModal();
                    } catch (error) {
                        this.showToast(error.response?.data?.errorMessage || "Something went wrong!", "error");
                    } finally {
                        this.budgetBusy = false;
                    }
                }
            });
        },
    },
};
</script>

<template>
    <b-modal size="lg" id="change-expenditure-modal" title="Change Expenditure" hide-footer no-close-on-esc
        no-close-on-backdrop>
        <div v-if="selectedResource" class="border py-2 px-3 mb-3">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                    <h5 class="mb-0 font-size-18 text-white">
                        {{ selectedResource.itemName }}
                    </h5>
                    <small class="text-muted" v-if="selectedResource.description">
                        {{ selectedResource.description }}
                    </small>
                </div>
            </div>

            <div v-if="selectedResource.financeTemplateBudgetItem" class="my-2">
                <small class="text-muted d-block font-size-11">Current Expenditure:</small>
                <span class="badge bg-purple text-white py-1 px-2 me-1 font-size-12">
                    {{ selectedResource.financeTemplateBudgetItem.fundClass }}
                </span>
                <span class="badge bg-success text-white py-1 px-2 font-size-12">
                    {{ selectedResource.financeTemplateBudgetItem.accountGroup }}
                </span>
            </div>
        </div>

        <div class="p-2">
            <b-row class="mb-3">
                <b-col sm="6" class="d-flex align-items-center">
                    <div class="d-flex align-items-center">
                        <h4 class="mb-0 font-size-16">List of Budget Line Items</h4>
                    </div>
                </b-col>
                <b-col sm="6" class="text-right">
                    <div class="d-flex float-end">
                        <div id="tickets-table_filter" class="dataTables_filter me-1">
                            <label class="d-inline-flex align-items-center mb-0">
                                <b-form-input autocomplete="off" v-model="filterBudget" type="search"
                                    placeholder="Search Account..." class="form-control"></b-form-input>
                            </label>
                        </div>
                    </div>
                </b-col>
            </b-row>
            <div class="table-responsive">
                <table class="table table-centered align-middle project-list-table border-collapse-none">
                    <thead>
                        <tr>
                            <th scope="col" width="50px"></th>
                            <th scope="col">Account Name</th>
                            <th scope="col" width="160px" class="text-center">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="budgetBusy">
                            <td colspan="100%">
                                <div class="d-flex justify-content-center align-items-center py-4">
                                    <div class="preloader-component me-2">
                                        <div class="status">
                                            <div class="spinner-chase w-20px h-20px">
                                                <div class="chase-dot"></div>
                                                <div class="chase-dot"></div>
                                                <div class="chase-dot"></div>
                                                <div class="chase-dot"></div>
                                                <div class="chase-dot"></div>
                                                <div class="chase-dot"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <strong>Loading...</strong>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="availableAccounts.length == 0 && !budgetBusy">
                            <td colspan="100%" class="text-muted text-center py-4">
                                <i class="dripicons-warning me-1"></i>No data found
                            </td>
                        </tr>
                        <tr v-if="filteredBudget.length == 0 && filterBudget && !budgetBusy">
                            <td colspan="100%" class="text-muted text-center py-4">
                                <i class="dripicons-warning me-1"></i>Search not found
                            </td>
                        </tr>
                        <template v-for="y in filteredBudget">
                            <tr :key="'A' + y.fundClassificationId"
                                style="background-color: transparent; box-shadow: none;">
                                <td colspan="100%" class="pad-2rem">
                                    <div class="d-flex align-items-center cursor-pointer py-2"
                                        @click="y.showData = !y.showData">
                                        <span class="bx text-warning me-1"
                                            :class="{ 'bx-plus': !y.showData && !filterBudget, 'bx-minus': y.showData || filterBudget }"
                                            style="line-height: unset;"></span>
                                        <h5 class="text-dark mb-0 font-size-14">
                                            {{ y.fundClassification.classification }}
                                        </h5>
                                    </div>
                                </td>
                            </tr>
                            <template v-if="y.showData || (filterBudget && filterBudget.trim() != '')">
                                <template v-for="(z, zIndex) in filteredBudgetChild(y.accounts)">
                                    <tr class="classic-tr" :key="'B' + z.id">
                                        <td class="pl-5px position-relative">
                                            <div class="line-vertical-middle"
                                                :class="{ 'bottom-50': filteredBudgetChild(y.accounts).length - 1 == zIndex }">
                                            </div>
                                            <div class="d-flex align-items-center">
                                                <div class="line-horizontal-middle w-100"></div>
                                            </div>
                                        </td>
                                        <td class="bg-table-tr" :class="{ 'border-top-white': zIndex > 0 }">
                                            <h5 class="text-truncate font-size-14 mb-0 d-flex">
                                                <a href="javascript: void(0);" class="text-dark"
                                                    @click="transferHere(z)" v-if="transferIfs(z)">
                                                    {{ z.groupName }}
                                                </a>
                                                <span v-else class="text-dark font-weight-bold">
                                                    {{ z.groupName }}
                                                </span>
                                            </h5>
                                        </td>
                                        <td class="text-center bg-table-tr" :class="{ 'border-top-white': zIndex > 0 }">
                                            <div class="d-flex justify-content-center">
                                                <span v-if="!transferIfs(z)"
                                                    class="badge bg-soft bg-success text-success font-size-12 py-1 px-2">
                                                    <i class="bx bx-check me-1"></i>Current Account
                                                </span>
                                                <a href="javascript:void(0)" @click="transferHere(z)" v-else
                                                    class="d-flex align-items-center text-success">
                                                    <span>Transfer Here</span>
                                                    <i class="
                                                            fas
                                                            fa-hand-point-left
                                                            pointing-animation
                                                            text-white
                                                            font-size-18
                                                            text-primary
                                                            cursor-pointer
                                                            ms-1
                                                        "></i>
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                        </template>
                    </tbody>
                </table>
            </div>
            <div class="text-end mt-3 border-top pt-2">
                <button type="button" class="btn btn-secondary" @click="closeModal">
                    Close
                </button>
            </div>
        </div>
    </b-modal>
</template>
